import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetBatchVouchersQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import Pagination from '../../../components/Pagination';
import env from '../../../config/env';
import { useAppSelector } from '../../../hooks/redux';

interface AdminBatchVouchersProps {
  type: 'card' | 'voucher';
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = date.getDate().toString().padStart(2, '0');
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month}, ${year}`;
};

const formatVoucherId = (id: number | string): string => {
  const numericId = Number(id);
  if (!numericId || numericId <= 0) {
    return `VA-${id}`;
  }
  return `VA-${numericId.toString().padStart(6, '0')}`;
};

const AdminBatchVouchers: React.FC<AdminBatchVouchersProps> = ({ type }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const token = useAppSelector((state) => state.auth.token);

  const { data, isLoading, error } = useGetBatchVouchersQuery({ 
    batchId: id, 
    params: { page, search } 
  }, {
    skip: !id
  });

  const title = type === 'card' ? 'Fesilah Cards' : 'Fesilah Vouchers';
  const listPath = type === 'card' ? '/admin/fesilah-cards' : '/admin/fesilah-vouchers';

  useEffect(() => {
    setPage(1);
  }, [search]);

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return <div className="alert alert-danger m-5">Error loading vouchers</div>;
  }

  const vouchers = data?.data || { data: [], current_page: 1, last_page: 1 };
  const batchName = data?.batch_name || 'Batch Vouchers';

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'new':
        return <span className="badge badge-light-success">New</span>;
      case 'used':
        return <span className="badge badge-light-primary">Used</span>;
      case 'expired':
        return <span className="badge badge-light-danger">Expired</span>;
      default:
        return <span className="badge badge-light-secondary">{status}</span>;
    }
  };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">{batchName}</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <span className="text-muted text-hover-primary">Home</span>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <button
                    type="button"
                    onClick={() => navigate(listPath)}
                    className="btn btn-link px-0 text-muted text-hover-primary"
                  >
                    {title}
                  </button>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Vouchers</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <div className="d-flex align-items-center position-relative my-1">
                    <span className="svg-icon svg-icon-1 position-absolute ms-6">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor"></rect>
                        <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor"></path>
                      </svg>
                    </span>
                    <input
                      type="text"
                      className="form-control form-control-solid w-250px ps-15"
                      placeholder="Search Voucher ID..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
                <div className="card-toolbar d-flex gap-3">
                  <button className="btn btn-light" onClick={() => navigate(listPath)}>
                    Back to Batches
                  </button>
                  {id && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={async () => {
                        if (!token) {
                          return;
                        }
                        try {
                          const basePath = env.apiUrl;
                          const normalizedBase = basePath.replace(/\/$/, '');
                          const url = `${normalizedBase}/admin/voucher-batches/${id}/vouchers/export`;

                          const response = await fetch(url, {
                            headers: {
                              Authorization: `Bearer ${token}`,
                              Accept: 'text/csv,application/octet-stream,*/*',
                            },
                          });

                          if (!response.ok) {
                            return;
                          }

                          const blob = await response.blob();
                          const downloadUrl = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = downloadUrl;
                          link.download = `voucher-batch-${id}-vouchers.csv`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(downloadUrl);
                        } catch {
                          // Silent fail; could add toast/logging if needed
                        }
                      }}
                    >
                      Export to Excel
                    </button>
                  )}
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th>#</th>
                        <th>Voucher ID</th>
                        <th>Voucher Code</th>
                        <th>Status</th>
                        <th>Activated By</th>
                        <th>Activation Date</th>
                        <th>Created Date</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {vouchers.data.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center py-10">
                            <span className="text-muted">No vouchers found</span>
                          </td>
                        </tr>
                      ) : (
                        vouchers.data.map((voucher: any, index: number) => (
                          <tr key={voucher.id}>
                            <td>{(vouchers.current_page - 1) * 15 + index + 1}</td>
                            <td>{formatVoucherId(voucher.id)}</td>
                            <td>{voucher.voucher_code_hash}</td>
                            <td>{getStatusBadge(voucher.status)}</td>
                            <td>
                                {voucher.user ? (
                                    <div className="d-flex align-items-center">
                                        <div className="d-flex justify-content-start flex-column">
                                            <span className="text-dark fw-bold text-hover-primary mb-1 fs-6">{voucher.user.name}</span>
                                            <span className="text-muted fw-semibold text-muted d-block fs-7">{voucher.user.email}</span>
                                        </div>
                                    </div>
                                ) : (
                                    '-'
                                )}
                            </td>
                            <td>{formatDate(voucher.activation_date)}</td>
                            <td>{formatDate(voucher.created_at)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {vouchers.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={vouchers.current_page}
                      lastPage={vouchers.last_page}
                      total={vouchers.total || 0}
                      perPage={vouchers.per_page || 15}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBatchVouchers;
